import { useEffect, useMemo, useState } from 'react';
import { listarPedidosDesde } from '@/features/pedidos/api';
import { dataHojeISO } from '@/features/pedidos/store';
import type { Pedido } from '@/features/pedidos/types';
import KPITile from '@/features/dashboard/KPITile';
import RevenueSparkline from '@/features/dashboard/RevenueSparkline';
import StatusDonut from '@/features/dashboard/StatusDonut';
import TopItemsBar from '@/features/dashboard/TopItemsBar';
import PeakHoursHeatmap from '@/features/dashboard/PeakHoursHeatmap';
import Money from '@/components/Money';
import { formatBRL, parseMoeda } from '@/lib/format';

function parseTotal(t: string | null | undefined): number {
  const n = parseMoeda(t);
  return Number.isFinite(n) ? n : 0;
}

function isoDay(iso: string): string {
  return iso.slice(0, 10);
}

export default function DashboardPage() {
  // Dashboard lê os últimos 90 dias (cobre métricas de hoje, 30 dias, donut,
  // mais-vendidos e heatmap) sem varrer o histórico inteiro — que cresce sem limite.
  const [pedidos, setPedidos] = useState<Pedido[]>([]);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    (async () => {
      try {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        const desde = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const lista = await listarPedidosDesde(desde, ac.signal);
        if (alive) setPedidos(lista);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.error('[Dashboard] falha ao carregar histórico:', err);
      }
    })();
    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const metrics = useMemo(() => {
    const hoje = dataHojeISO();
    const ontemDate = new Date();
    ontemDate.setDate(ontemDate.getDate() - 1);
    const ontem = `${ontemDate.getFullYear()}-${String(ontemDate.getMonth() + 1).padStart(2, '0')}-${String(ontemDate.getDate()).padStart(2, '0')}`;

    const dehoje = pedidos.filter((p) => isoDay(p.criado_em) === hoje);
    const deontem = pedidos.filter((p) => isoDay(p.criado_em) === ontem);

    const receitaHoje = dehoje.reduce((s, p) => s + parseTotal(p.total), 0);
    const receitaOntem = deontem.reduce((s, p) => s + parseTotal(p.total), 0);

    const ticketMedioHoje = dehoje.length ? receitaHoje / dehoje.length : 0;

    // sparkline 30 dias (datas locais, não UTC)
    const dias: string[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      dias.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    const valoresPorDia = dias.map((dia) =>
      pedidos.filter((p) => isoDay(p.criado_em) === dia).reduce((s, p) => s + parseTotal(p.total), 0),
    );
    const receita30 = valoresPorDia.reduce((s, x) => s + x, 0);

    // donut
    const counts = {
      recebido: 0,
      pago: 0,
      em_separacao: 0,
      em_rota: 0,
      entregue: 0,
      cancelado: 0,
    };
    for (const p of pedidos) counts[p.status] = (counts[p.status] ?? 0) + 1;

    const segmentos = [
      { label: 'Pedido novo', valor: counts.recebido, cor: 'var(--amber-500)' },
      { label: 'Aguardando pagamento', valor: counts.pago, cor: 'var(--moss-300)' },
      { label: 'Em separação', valor: counts.em_separacao, cor: 'var(--sage-500)' },
      { label: 'Em rota', valor: counts.em_rota, cor: 'var(--soil-500)' },
      { label: 'Entregue', valor: counts.entregue, cor: 'var(--fg-3)' },
    ].filter((s) => s.valor > 0);

    // top itens
    const tally = new Map<string, number>();
    for (const p of pedidos) {
      for (const it of p.itens || []) {
        const k = it.descricao;
        tally.set(k, (tally.get(k) ?? 0) + (it.qtd ?? 1));
      }
    }
    const topItens = [...tally.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, valor]) => ({ label, valor }));

    // heatmap 7×24
    const heat: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const p of pedidos) {
      const d = new Date(p.criado_em.replace(' ', 'T'));
      if (isNaN(d.getTime())) continue;
      heat[d.getDay()][d.getHours()] += 1;
    }

    return {
      hojeCount: dehoje.length,
      ontemCount: deontem.length,
      receitaHoje,
      receitaOntem,
      ticketMedioHoje,
      valoresPorDia,
      receita30,
      segmentos,
      topItens,
      heat,
    };
  }, [pedidos]);

  const deltaPedidos = metrics.hojeCount - metrics.ontemCount;
  const deltaReceita = metrics.receitaHoje - metrics.receitaOntem;

  return (
    <div className="flex flex-col gap-s7">
      <header>
        <h1 className="t-overline mb-s3">Dashboard</h1>
        <p className="font-display italic text-[40px] leading-tight text-fg-1 tracking-[-0.02em]">
          O jardim em números.
        </p>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-s5">
        <KPITile
          label="Pedidos hoje"
          valor={metrics.hojeCount}
          delta={
            deltaPedidos !== 0
              ? {
                  sinal: deltaPedidos > 0 ? '+' : '-',
                  texto: `${Math.abs(deltaPedidos)} vs ontem`,
                }
              : undefined
          }
        />
        <KPITile
          label="Receita hoje"
          valor={metrics.receitaHoje}
          moeda
          delta={
            deltaReceita !== 0
              ? {
                  sinal: deltaReceita > 0 ? '+' : '-',
                  texto: formatBRL(Math.abs(deltaReceita)).replace(/^R\$\s/, ''),
                }
              : undefined
          }
        />
        <KPITile label="Ticket médio" valor={metrics.ticketMedioHoje} moeda />
        <KPITile label="Receita 30 dias" valor={metrics.receita30} moeda />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-s5">
        <div className="lg:col-span-2 bg-panel border border-[var(--border)] rounded-md p-s7">
          <div className="flex items-baseline justify-between mb-s5">
            <span className="t-overline">Receita 30 dias</span>
            <Money valor={metrics.receita30} size="lg" />
          </div>
          <RevenueSparkline valores={metrics.valoresPorDia} />
        </div>
        <div className="bg-panel border border-[var(--border)] rounded-md p-s7">
          <span className="t-overline">Status agora</span>
          <div className="mt-s5">
            {metrics.segmentos.length === 0 ? (
              <p className="font-display italic text-[18px] text-fg-3 text-center mt-s5">
                Sem pedidos.
              </p>
            ) : (
              <StatusDonut segmentos={metrics.segmentos} />
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-s5">
        <div className="bg-panel border border-[var(--border)] rounded-md p-s7">
          <span className="t-overline">Mais vendidos</span>
          <div className="mt-s5">
            {metrics.topItens.length === 0 ? (
              <p className="font-display italic text-[16px] text-fg-3 text-center mt-s5">
                Nada vendido ainda.
              </p>
            ) : (
              <TopItemsBar itens={metrics.topItens} />
            )}
          </div>
        </div>
        <div className="bg-panel border border-[var(--border)] rounded-md p-s7">
          <span className="t-overline">Horários de pico</span>
          <div className="mt-s5">
            <PeakHoursHeatmap matriz={metrics.heat} />
          </div>
        </div>
      </section>
    </div>
  );
}
